import type { BrandOrganization } from "src/api/client-api/brand/brandSchemas";
import type { BrandPatch, BrandWrite } from "src/api/client-api/brand/useCreateBrand";
import { getApiViolations } from "src/utils/apiViolations";

export type BrandAccessSummary =
    | { kind: "owner-selection" }
    | { kind: "all-subsidiaries" }
    | { kind: "shared-by"; ownerName: string };

type BrandAccess = {
    organization: BrandOrganization;
    sharedWithAllSubsidiaries: boolean;
    canModify: boolean;
};

export const getBrandAccessSummary = (
    brand: BrandAccess,
    viewerOrganizationIri: IRI | undefined,
): BrandAccessSummary => {
    if (brand.organization["@id"] !== viewerOrganizationIri && !brand.canModify) {
        return { kind: "shared-by", ownerName: brand.organization.name };
    }

    if (brand.sharedWithAllSubsidiaries) {
        return { kind: "all-subsidiaries" };
    }

    return { kind: "owner-selection" };
};

export const mergeVisibleSelection = (selected: IRI[], visible: IRI[], visibleSelected: IRI[]): IRI[] => [
    ...selected.filter((iri) => !visible.includes(iri)),
    ...visibleSelected,
];

export type BrandFormValues = {
    name: string;
    organization: IRI | undefined;
    sharedWithOrganizations: IRI[];
    sharedWithAllSubsidiaries: boolean;
};

const haveSameMembers = (left: IRI[], right: IRI[]) =>
    left.length === right.length && left.every((iri) => right.includes(iri));

export const buildBrandPatch = (id: number, initial: BrandFormValues, current: BrandFormValues): BrandPatch => ({
    id,
    ...(current.name !== initial.name && { name: current.name }),
    ...(current.organization !== initial.organization &&
        current.organization !== undefined && { organization: current.organization }),
    ...(!haveSameMembers(current.sharedWithOrganizations, initial.sharedWithOrganizations) && {
        sharedWithOrganizations: current.sharedWithOrganizations,
    }),
    ...(current.sharedWithAllSubsidiaries !== initial.sharedWithAllSubsidiaries && {
        sharedWithAllSubsidiaries: current.sharedWithAllSubsidiaries,
    }),
});

export const buildBrandCreation = (values: BrandFormValues & { organization: IRI }): BrandWrite => ({
    name: values.name,
    organization: values.organization,
    sharedWithOrganizations: values.sharedWithOrganizations,
    sharedWithAllSubsidiaries: values.sharedWithAllSubsidiaries,
});

const ACCESS_PROPERTY_PATH = "sharedWithOrganizations";

export const getAccessViolationMessage = (error: unknown): string | null => {
    const messages = (getApiViolations(error) ?? [])
        .filter((violation) => violation.propertyPath?.startsWith(ACCESS_PROPERTY_PATH))
        .map((violation) => violation.message);

    return messages.length > 0 ? messages.join(" ") : null;
};
